import { useEffect, useMemo, useState } from "react";
import type { CSSProperties, ReactNode } from "react";
import { Select, Spin, theme } from "antd";
import type { SelectProps } from "antd";
import { TbAlertTriangle, TbChevronDown, TbSettings } from "react-icons/tb";
import { LifeCycle, StageDto } from "../../dtos/stage.dto";
import { LIFECYCLE_META, LIFECYCLE_ORDER, resolveStageColor, resolveStageIcon } from "./constants";
import "./select.scss";

type StageSelectSize = "small" | "middle" | "large";

export interface StageSelectProps {
  value?: string | null;
  stages: StageDto[];
  onChange?: (stage?: StageDto) => void;
  groupByLifecycle?: boolean;
  showOptionKey?: boolean;
  placeholder?: string;
  disabled?: boolean;
  loading?: boolean;
  allowClear?: boolean;
  size?: StageSelectSize;
  status?: SelectProps["status"];
  style?: CSSProperties;
  className?: string;
  maxLabelWidth?: number;
}

interface StageOption {
  value: string;
  label: ReactNode;
  searchText: string;
  disabled?: boolean;
  record?: StageDto;
}

const METRICS: Record<
  StageSelectSize,
  {
    chipHeight: number;
    chipFont: number;
    glyph: number;
    dot: number;
    optionLabel: number;
    optionKey: number;
    optionGap: number;
    popupWidth: number;
    itemHeight: number;
    listHeight: number;
    twoLine: boolean;
  }
> = {
  small: {
    chipHeight: 22,
    chipFont: 12,
    glyph: 13,
    dot: 18,
    optionLabel: 12,
    optionKey: 10,
    optionGap: 8,
    popupWidth: 208,
    itemHeight: 28,
    listHeight: 224,
    twoLine: false,
  },
  middle: {
    chipHeight: 25,
    chipFont: 12,
    glyph: 13,
    dot: 22,
    optionLabel: 13,
    optionKey: 11,
    optionGap: 10,
    popupWidth: 260,
    itemHeight: 44,
    listHeight: 264,
    twoLine: true,
  },
  large: {
    chipHeight: 28,
    chipFont: 13,
    glyph: 15,
    dot: 26,
    optionLabel: 14,
    optionKey: 11,
    optionGap: 10,
    popupWidth: 300,
    itemHeight: 48,
    listHeight: 288,
    twoLine: true,
  },
};

const StageDot = ({ stage, size }: { stage: StageDto; size: number }) => {
  const swatch = resolveStageColor(stage.color);
  const Icon = resolveStageIcon(stage.icon);

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        width: size,
        height: size,
        flexShrink: 0,
        borderRadius: "50%",
        background: swatch.solid,
        color: "#fff",
      }}
    >
      <Icon size={Math.round(size * 0.62)} />
    </span>
  );
};

const StageChip = ({
  icon,
  children,
  bg,
  border,
  text,
  height,
  fontSize,
  maxLabelWidth,
}: {
  icon: ReactNode;
  children: ReactNode;
  bg: string;
  border: string;
  text: string;
  height: number;
  fontSize: number;
  maxLabelWidth: number;
}) => (
  <span
    className="stage-select__chip"
    style={{
      display: "inline-flex",
      alignItems: "center",
      verticalAlign: "middle",
      gap: 5,
      maxWidth: "100%",
      height,
      paddingInline: Math.round(height / 3),
      borderRadius: 999,
      fontSize,
      fontWeight: 500,
      lineHeight: 1,
      background: bg,
      color: text,
      boxShadow: `inset 0 0 0 1px ${border}`,
    }}
  >
    <span style={{ display: "flex", flexShrink: 0 }}>{icon}</span>
    <span
      style={{
        display: "block",
        minWidth: 0,
        maxWidth: maxLabelWidth,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </span>
    <TbChevronDown className="stage-select__chevron" size={Math.round(fontSize)} />
  </span>
);

const StageSelect = ({
  value: controlledValue,
  stages,
  onChange,
  groupByLifecycle = true,
  showOptionKey,
  placeholder = "Select a stage",
  disabled,
  loading,
  allowClear,
  size = "small",
  status,
  style,
  className,
  maxLabelWidth = 160,
}: StageSelectProps) => {
  const { token } = theme.useToken();
  const [override, setOverride] = useState<string | undefined>();

  useEffect(() => setOverride(undefined), [controlledValue]);

  const value = override ?? controlledValue ?? undefined;
  const m = METRICS[size];
  const withKey = showOptionKey ?? m.twoLine;

  const byId = useMemo(() => {
    const map = new Map<string, StageDto>();
    stages.forEach((stage) => map.set(String(stage._id), stage));
    return map;
  }, [stages]);

  const unresolved = !loading && Boolean(value) && !byId.has(String(value));
  const pending = Boolean(loading) && Boolean(value) && !byId.has(String(value));

  const options = useMemo(() => {
    const selectable = stages.filter((stage) => !stage.archived || String(stage._id) === value);

    const toOption = (stage: StageDto): StageOption => ({
      value: String(stage._id),
      searchText: `${stage.label} ${stage.value}`.toLowerCase(),
      disabled: stage.archived,
      record: stage,
      label: stage.label,
    });

    const sorted = selectable.slice().sort((a, b) => a.label.localeCompare(b.label));

    const placeholderOption = (label: ReactNode): StageOption[] => [
      { value: String(value), searchText: String(value).toLowerCase(), label },
    ];

    const missing: StageOption[] = pending
      ? placeholderOption("Loading…")
      : unresolved
        ? placeholderOption("Unknown stage")
        : [];

    if (!groupByLifecycle) {
      return [...missing, ...sorted.map(toOption)];
    }

    const groups = LIFECYCLE_ORDER.map((lifecycle) => {
      const items = sorted.filter((stage) => stage.lifecycle === lifecycle);
      if (items.length === 0) return null;
      const meta = LIFECYCLE_META[lifecycle];
      return {
        label: (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}>
            <meta.Icon size={size === "small" ? 11 : 13} />
            {meta.label}
          </span>
        ),
        title: meta.label,
        options: items.map(toOption),
      };
    }).filter(Boolean) as SelectProps["options"];

    const orphans = sorted.filter((stage) => !LIFECYCLE_ORDER.includes(stage.lifecycle as LifeCycle)).map(toOption);

    return [...missing, ...(groups ?? []), ...orphans];
  }, [stages, value, groupByLifecycle, unresolved, pending, size]);

  const labelRender: SelectProps["labelRender"] = ({ value: current }) => {
    const metrics = { height: m.chipHeight, fontSize: m.chipFont, maxLabelWidth };

    if (pending) {
      return (
        <StageChip
          {...metrics}
          icon={<Spin size="small" />}
          bg={token.colorFillQuaternary}
          border={token.colorBorderSecondary}
          text={token.colorTextTertiary}
        >
          Loading…
        </StageChip>
      );
    }

    const stage = current ? byId.get(String(current)) : undefined;

    if (!stage) {
      return (
        <StageChip
          {...metrics}
          icon={<TbAlertTriangle size={m.glyph} />}
          bg={token.colorWarningBg}
          border={token.colorWarningBorder}
          text={token.colorWarningText}
        >
          Unknown stage
        </StageChip>
      );
    }

    const swatch = resolveStageColor(stage.color);
    const Icon = resolveStageIcon(stage.icon);

    return (
      <StageChip {...metrics} icon={<Icon size={m.glyph} />} bg={swatch.bg} border={swatch.border} text={swatch.text}>
        {stage.label}
      </StageChip>
    );
  };

  return (
    <Select
      value={value}
      onChange={(next: string) => {
        setOverride(next);
        onChange?.(byId.get(next));
      }}
      options={options}
      placeholder={loading ? "Loading…" : placeholder}
      disabled={disabled || loading}
      loading={loading}
      allowClear={allowClear}
      size={size}
      status={unresolved ? "warning" : status}
      variant="borderless"
      suffixIcon={null}
      labelRender={labelRender}
      className={["stage-select", "stage-select--no-search", className].filter(Boolean).join(" ")}
      popupClassName={`stage-select-popup stage-select-popup--${size}`}
      style={style}
      popupMatchSelectWidth={m.popupWidth}
      listItemHeight={m.itemHeight}
      listHeight={m.listHeight}
      optionRender={(option) => {
        const stage = (option.data as StageOption).record;
        if (!stage) return option.label;

        return (
          <span style={{ display: "flex", alignItems: "center", gap: m.optionGap, minWidth: 0 }}>
            <StageDot stage={stage} size={m.dot} />
            <span style={{ minWidth: 0, flex: 1 }}>
              <span
                style={{
                  display: "block",
                  fontSize: m.optionLabel,
                  lineHeight: 1.4,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {stage.label}
                {stage.archived ? (
                  <span style={{ marginLeft: 6, color: token.colorTextTertiary }}>· archived</span>
                ) : null}
              </span>
              {withKey ? (
                <span
                  style={{
                    display: "block",
                    fontSize: m.optionKey,
                    lineHeight: 1.4,
                    fontFamily: token.fontFamilyCode,
                    color: token.colorTextTertiary,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {stage.value}
                </span>
              ) : null}
            </span>
            {stage.locked ? <TbSettings size={size === "small" ? 12 : 14} color={token.colorTextTertiary} /> : null}
          </span>
        );
      }}
      notFoundContent={
        <div style={{ padding: "8px 4px", color: token.colorTextTertiary, fontSize: 13 }}>No stage matches that.</div>
      }
    />
  );
};

export default StageSelect;
